import React, { useEffect, useState } from "react";
import { Box, Typography, Link as MuiLink, Paper, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getContactUs } from "../services/api/getAll";
import bgImage from "../assets/kiki2.jpeg";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TikTokIcon from "@mui/icons-material/MusicNote";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RoomIcon from "@mui/icons-material/Room";

const footerLinks = [
  { label: "home", href: "#home" },
  { label: "services", href: "#services" },
  { label: "bookService", href: "#booking-section" },
  { label: "aboutUs", href: "#about" },
  { label: "missionVision", href: "#mission-vision" },
  { label: "ourCrew", href: "#crew" },
  { label: "joinUs", href: "#join" },
  { label: "serviceProviderAgreement", href: "/Service_provider_agreemnt.pdf", external: true, download: true },
  { label: "userAgreement", href: "/User_agreement.pdf", external: true, download: true },
  { label: "driverAgreement", href: "/Driver_agreement.pdf", external: true, download: true },
  { label: "clientAgreement", href: "/Client_agreement.pdf", external: true, download: true }
];

const ContactFooter = ({ Brands }: any) => {
  const { t, i18n } = useTranslation();
  const currentLang = localStorage.getItem("language") || "en";
  const isRTL = i18n.language === "ar";

  const [contact, setContact] = useState({
    address: "",
    phone: "",
    email: "",
    mapEmbedUrl: "",
  });

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await getContactUs();
        const item =
          response.items?.find((i: any) => i.languageCode === currentLang) ||
          response.items?.[0];

        if (item) {
          setContact({
            address: item.address,
            phone: item.phone,
            email: item.email,
            mapEmbedUrl: item.mapEmbedUrl || "",
          });
        }
      } catch (err) {
        console.error("Failed to fetch contact info:", err);
      }
    };

    fetchContact();
  }, [currentLang]);

  return (
    <Box
      id="contact"
      sx={{
        background: "transparent",
        color: "#fff",
        overflow: "hidden",
        position: "relative",
      }}
    >

      
      {/* Contact content layout */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9 }}
        style={{ padding: "0 1rem", position: "relative", zIndex: 2 }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 2, md: 4 },
            pt: 12,
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight={900}
            mb={2}
            sx={{
              fontFamily: "Poppins, Ozen",
              background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textTransform: "uppercase",
              fontSize: { xs: "1.8rem", md: "2.5rem" },
              textShadow: "0 0 30px rgba(255, 174, 0, 0.1)",
            }}
          >
            {t("contactFooter.getInTouch")}
          </Typography>

          <Typography
            variant="body1"
            dir={isRTL ? "rtl" : "ltr"}
            sx={{
              color: "#aaa",
              maxWidth: "700px",
              mx: "auto",
              mb: 6,
              fontSize: { xs: "0.95rem", md: "1.1rem" },
              fontFamily: "Poppins",
              lineHeight: 1.6,
              textAlign: "center",
            }}
          >
            {t("contactFooter.description")}
          </Typography>

          {/* Cards for Address, Phone, Email */}
          <Grid container spacing={3} sx={{ mb: 8, direction: isRTL ? "rtl" : "ltr" }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="premium-service-card"
                sx={{
                  p: 3,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <PhoneAndroidIcon sx={{ color: "#FFAE00", fontSize: 36, mb: 1.5 }} />
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "Poppins", mb: 1, color: "#FFAE00" }}>
                  {isRTL ? "الهاتف" : "Phone"}
                </Typography>
                <Typography variant="body1" dir="ltr" sx={{ fontFamily: "Poppins", color: "#e0e0e0", fontWeight: 500 }}>
                  {contact.phone || "+1 (800) 123-4567"}
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="premium-service-card"
                sx={{
                  p: 3,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <MailOutlineIcon sx={{ color: "#FFAE00", fontSize: 36, mb: 1.5 }} />
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "Poppins", mb: 1, color: "#FFAE00" }}>
                  {isRTL ? "البريد الإلكتروني" : "Email"}
                </Typography>
                <Typography variant="body1" dir="ltr" sx={{ fontFamily: "Poppins", color: "#e0e0e0", fontWeight: 500 }}>
                  {contact.email || "info@towhelp.com"}
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="premium-service-card"
                sx={{
                  p: 3,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <RoomIcon sx={{ color: "#FFAE00", fontSize: 36, mb: 1.5 }} />
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "Poppins", mb: 1, color: "#FFAE00" }}>
                  {isRTL ? "العنوان" : "Address"}
                </Typography>
                <Typography variant="body1" dir={contact.address && /[\u0600-\u06FF]/.test(contact.address) ? "rtl" : "ltr"} sx={{ fontFamily: "Poppins", color: "#e0e0e0", fontSize: "0.95rem" }}>
                  {contact.address || "123 Main St, New York"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Map Section */}
          <Box
            sx={{
              width: "100%",
              height: { xs: "300px", md: "450px" },
              borderRadius: "24px",
              boxShadow: "0 15px 45px rgba(0, 0, 0, 0.6), 0 0 20px rgba(255, 174, 0, 0.1)",
              border: "1px solid rgba(255, 174, 0, 0.2)",
              overflow: "hidden",
              mb: 10,
              "& iframe": {
                width: "100%",
                height: "100%",
                border: 0,
              },
            }}
            dangerouslySetInnerHTML={{ __html: contact.mapEmbedUrl }}
          />
        </Box>
      </Box>

      {/* Footer layout */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        style={{
          background: "#050505",
          padding: "4rem 1rem 2rem",
          position: "relative",
          zIndex: 2,
          borderTop: "1px solid rgba(255, 255, 255, 0.05)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", md: "flex-start" },
            gap: 4,
            pt: 2,
            px: { xs: 2, md: 4 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              gap: 2,
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, md: 3 },
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {footerLinks.map((link) => (
                <MuiLink
                  key={link.label}
                  href={link.href}
                  underline="none"
                  target={link.external ? "_blank" : undefined}
                  rel={link.external ? "noopener noreferrer" : undefined}
                  download={link.download ? "" : undefined}
                  sx={{
                    color: "#bbb",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                    transition: "all 0.3s ease",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: "0%",
                      height: "1px",
                      bottom: "-2px",
                      left: 0,
                      backgroundColor: "#FFAE00",
                      transition: "width 0.3s ease",
                    },
                    "&:hover": {
                      color: "#FFAE00",
                      "&::after": {
                        width: "100%",
                      }
                    },
                  }}
                >
                  {t(`contactFooter.footerLinks.${link.label}`)}
                </MuiLink>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2.5,
                mt: 2,
              }}
            >
              {Brands?.facebookAccount && (
                <Box
                  component="a"
                  href={Brands.facebookAccount}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#fff",
                    transition: "color 0.3s ease, transform 0.3s ease",
                    "&:hover": { color: "#FFAE00", transform: "scale(1.15)" },
                  }}
                >
                  <FacebookIcon sx={{ fontSize: 28 }} />
                </Box>
              )}
              {Brands?.instagramAccount && (
                <Box
                  component="a"
                  href={Brands.instagramAccount}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#fff",
                    transition: "color 0.3s ease, transform 0.3s ease",
                    "&:hover": { color: "#FFAE00", transform: "scale(1.15)" },
                  }}
                >
                  <InstagramIcon sx={{ fontSize: 28 }} />
                </Box>
              )}
              {Brands?.tikTokAccount && (
                <Box
                  component="a"
                  href={Brands.tikTokAccount}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#fff",
                    transition: "color 0.3s ease, transform 0.3s ease",
                    "&:hover": { color: "#FFAE00", transform: "scale(1.15)" },
                  }}
                >
                  <TikTokIcon sx={{ fontSize: 28 }} />
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: { xs: "center", md: "flex-end" },
            }}
          >
            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (Brands?.appleStoreLink) {
                  window.open(Brands.appleStoreLink, "_blank", "noopener,noreferrer");
                }
              }}
              sx={{
                display: "inline-block",
                borderRadius: "8px",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": { transform: "translateY(-3px)", boxShadow: "0 6px 15px rgba(255, 174, 0, 0.15)" },
              }}
            >
              <Box
                component="img"
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                alt="App Store"
                sx={{ height: 38 }}
              />
            </Box>
            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (Brands?.googlePlayStoreLink) {
                  window.open(Brands.googlePlayStoreLink, "_blank", "noopener,noreferrer");
                }
              }}
              sx={{
                display: "inline-block",
                borderRadius: "8px",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": { transform: "translateY(-3px)", boxShadow: "0 6px 15px rgba(255, 174, 0, 0.15)" },
              }}
            >
              <Box
                component="img"
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Google Play"
                sx={{ height: 38 }}
              />
            </Box>
          </Box>
        </Box>

        <Typography
          variant="body2"
          mt={6}
          textAlign="center"
          sx={{
            color: "#555",
            fontFamily: "Poppins",
            fontSize: { xs: "0.75rem", md: "0.9rem" },
          }}
        >
          ©{new Date().getFullYear()} {t("contactFooter.copyright")}
        </Typography>
      </Box>
    </Box>
  );
};

export default ContactFooter;

